/**
 * The programmes an account can reach, and the modules assigned to it.
 *
 * @module components/overview/ProgrammeDirectory
 */

import React from "react";

import { recordVersionLabel } from "@/components/shared/revision-labels";
import { WorkflowStateBadge } from "@/components/shared/WorkflowStateBadge";
import {
  Alert,
  Badge,
  Button,
  CollectionView,
  type Column,
  EmptyState,
  EntityCard,
  Heading,
  KeyValue,
  ListRow,
  MutedText,
  Panel,
  Text,
  useTableSort,
} from "@/components/ui";
import type { AssignedModuleSummary, ProgrammeSummary } from "@/platform";

export interface ProgrammeDirectoryProps {
  /** Already scoped to what this account may see. */
  programmes: readonly ProgrammeSummary[];
  /** Omitted for an account whose work is not module-shaped. */
  modules?: readonly AssignedModuleSummary[];
  /** The reader, whose own modules are listed apart from the rest. */
  currentUserId?: string;
  /** Programmes the reader leads, whose every module is listed with its owner. */
  ledProgrammeIds?: readonly string[];
  title: string;
  error?: Error | null;
  /** The read has not answered: draw a skeleton, never the empty state. */
  loading?: boolean;
  onOpen?: (programmeId: string) => void;
}

/** One list of modules, each named with its programme and review status. */
function ModuleSection({
  title,
  rows,
  emptyTitle,
  owner = undefined,
  "data-testid": testId,
}: {
  title: string;
  rows: readonly AssignedModuleSummary[];
  emptyTitle: string;
  /** Who builds it, drawn as a badge; omitted where every row is the reader's. */
  owner?: (module: AssignedModuleSummary) => string;
  "data-testid": string;
}): React.ReactElement {
  return (
    <div className="mt-6" data-testid={testId}>
      <Heading level={3} size="md">
        {title}
      </Heading>
      {rows.length === 0 ? (
        <div className="mt-3">
          <EmptyState title={emptyTitle} density="row" />
        </div>
      ) : (
        <ul className="mt-3 flex flex-col gap-2">
          {rows.map((module) => (
            <li key={module.id}>
              <ListRow
                trailing={
                  <span className="flex flex-wrap items-center gap-2">
                    {owner ? (
                      <Badge tone={module.ownerUserId ? "neutral" : "warning"} size="sm">
                        {owner(module)}
                      </Badge>
                    ) : null}
                    <Badge tone="neutral" size="sm">
                      {module.moduleReviewStatus}
                    </Badge>
                  </span>
                }
              >
                <div className="font-medium">{module.moduleTitle}</div>
                <MutedText size="caption" as="div" className="mt-1">
                  {module.programmeTitle}
                </MutedText>
              </ListRow>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * @example
 * ```tsx
 * <ProgrammeDirectory title="Programme directory" programmes={scoped} onOpen={open} />
 * ```
 */
export function ProgrammeDirectory({
  programmes,
  modules = undefined,
  currentUserId = undefined,
  ledProgrammeIds = [],
  title,
  error = null,
  loading = false,
  onOpen = undefined,
}: ProgrammeDirectoryProps): React.ReactElement {
  // Every column declares a comparator, and `DataTable` draws no sort
  // control without a change handler, so without this pair they described
  // an ordering the table could never apply.
  const sort = useTableSort({ key: "title", direction: "asc" });
  // An empty list is the same shape whether the account may see nothing or the
  // read never answered, and only one of those is a statement about scope.
  const unanswered = Boolean(error);

  const columns: readonly Column<ProgrammeSummary>[] = [
    {
      key: "title",
      header: "Programme",
      sortValue: (programme) => programme.title,
      render: (programme) => (
        <>
          {onOpen ? (
            <Button
              variant="title"
              onClick={() => {
                onOpen(programme.id);
              }}
              // The one control E2E clicks to open a programme from here. The
              // old panel had a second, separate "Open programme" button; the
              // title is the control now, so it carries that testid.
              data-testid={`overview-open-programme-${programme.id}`}
            >
              {programme.title}
            </Button>
          ) : (
            <span className="font-semibold">{programme.title}</span>
          )}
          <Text as="div" size="sm" tone="muted">
            {recordVersionLabel(programme.saveRevision)}
          </Text>
        </>
      ),
    },
    {
      key: "state",
      header: "State",
      sortValue: (programme) => programme.workflowState,
      render: (programme) => <WorkflowStateBadge state={programme.workflowState} />,
    },
    {
      key: "award",
      header: "Award",
      sortValue: (programme) => programme.awardType,
      render: (programme) => programme.awardType,
      priority: 3,
    },
    {
      key: "level",
      header: "Level",
      sortValue: (programme) => programme.nfqLevel,
      render: (programme) => (programme.nfqLevel === null ? "—" : `NFQ ${programme.nfqLevel}`),
      align: "end",
      priority: 2,
    },
    {
      key: "modules",
      header: "Modules",
      sortValue: (programme) => programme.moduleCount,
      render: (programme) => programme.moduleCount,
      align: "end",
      priority: 2,
    },
  ];

  return (
    <Panel title={title} icon="stack" collapsible>
      {error ? <Alert tone="warning">{error.message}</Alert> : null}

      {/* No toggle: a six-column directory is worth a table where one fits
          and cards where one does not, and the reader has no stake in which.
          The same arrangement the module review report uses. */}
      <CollectionView
        loading={loading}
        loadingLabel="Loading programmes"
        storageKey="programme-directory"
        {...sort}
        caption={title}
        columns={columns}
        rows={programmes}
        rowKey={(programme) => programme.id}
        rowTestId={(programme) => `overview-programme-row-${programme.id}`}
        emptyState={
          <EmptyState
            icon="graduation-cap"
            title={
              unanswered
                ? "The programme list is not available yet"
                : "No programmes are available to this account"
            }
            density="row"
          />
        }
        cards={{
          render: (programme) => (
            <EntityCard
              title={
                onOpen ? (
                  <Button
                    variant="title"
                    onClick={() => {
                      onOpen(programme.id);
                    }}
                    // The table's id, not a second one: `CollectionView`
                    // renders the table or the cards and never both, so the
                    // id E2E clicks has to be on whichever is on screen.
                    data-testid={`overview-open-programme-${programme.id}`}
                  >
                    {programme.title}
                  </Button>
                ) : (
                  programme.title
                )
              }
              data-testid={`overview-programme-card-${programme.id}`}
            >
              <WorkflowStateBadge state={programme.workflowState} />
              {/* Only the facts this programme has. A card that spells every
                  column spends most of its height on em dashes. */}
              <KeyValue
                className="mt-3"
                layout="inline"
                entries={[
                  { label: "Award", value: programme.awardType },
                  {
                    label: "Level",
                    value: programme.nfqLevel === null ? null : `NFQ ${programme.nfqLevel}`,
                  },
                  { label: "Modules", value: programme.moduleCount },
                  { label: "Record version", value: programme.saveRevision },
                ].filter((entry) => entry.value !== null && entry.value !== "")}
              />
            </EntityCard>
          ),
          "data-testid": "overview-programme-directory-cards",
        }}
        data-testid="overview-programme-directory"
      />

      {modules && (
        <ModuleSection
          title="Your modules"
          rows={modules.filter((module) => module.ownerUserId === currentUserId)}
          emptyTitle={
            unanswered ? "Your modules are not available yet" : "No modules are assigned to you"
          }
          data-testid="overview-your-modules"
        />
      )}
      {/* A lead's programmes list every module, so the heading above cannot
          hold them: most are somebody else's or nobody's yet. */}
      {modules && ledProgrammeIds.length > 0 && (
        <ModuleSection
          title="Programme modules"
          rows={modules.filter((module) => ledProgrammeIds.includes(module.programmeId))}
          emptyTitle={
            unanswered
              ? "The programme modules are not available yet"
              : "The programmes you lead have no modules yet"
          }
          owner={(module) => {
            if (!module.ownerUserId) {
              return "Unassigned";
            }
            return module.ownerUserId === currentUserId
              ? "You"
              : (module.ownerName ?? "Unknown builder");
          }}
          data-testid="overview-programme-modules"
        />
      )}
    </Panel>
  );
}
